import { zIndex } from 'constants/ui'
import { type FC, type ReactNode, memo, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { Tooltip as BaseTooltip, type PlacesType } from 'react-tooltip'
import { useTheme } from 'styled-components'

type TooltipProps = {
	id: string
	trigger: ReactNode
	content: ReactNode
	place?: PlacesType
	onTriggerHover?: () => void
	show?: boolean
	portal?: boolean
}

const Tooltip: FC<TooltipProps> = memo(
	({ id, trigger, content, place = 'top', show = true, portal = false, ...props }) => {
		const theme = useTheme()
		const [tooltipPortal, setTooltipPortal] = useState<HTMLElement>()
		const isFirefox = useMemo(() => /firefox/i.test(navigator.userAgent), [])

		useEffect(() => {
			let tooltipPortal = document.getElementById('tooltip-portal')
			if (!tooltipPortal) {
				tooltipPortal = document.createElement('div')
				tooltipPortal.id = 'tooltip-portal'
				document.body.appendChild(tooltipPortal)
			}
			setTooltipPortal(tooltipPortal)
		}, [])

		const tooltipComponent = useMemo(
			() => (
				<BaseTooltip
					id={id}
					style={{
						backgroundColor: theme.colors.selectedTheme.newTheme.tooltip.background,
						borderRadius: '6px',
						minWidth: '200px',
						maxWidth: '300px',
						marginTop: isFirefox ? '-60px' : 'initial',
						zIndex: zIndex.DRAWER,
					}}
					border={theme.colors.selectedTheme.newTheme.tooltip.border}
					place={place}
					className="tooltip"
					opacity={1}
				>
					{content}
				</BaseTooltip>
			),
			[
				content,
				id,
				isFirefox,
				place,
				theme.colors.selectedTheme.newTheme.tooltip.background,
				theme.colors.selectedTheme.newTheme.tooltip.border,
			]
		)

		return (
			<div {...props}>
				<div data-tooltip-id={id} onMouseEnter={props.onTriggerHover}>
					{trigger}
				</div>
				{show
					? portal
						? tooltipPortal && ReactDOM.createPortal(tooltipComponent, tooltipPortal)
						: tooltipComponent
					: null}
			</div>
		)
	}
)

export default Tooltip
